import React, {
  Fragment,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import Calendar from "react-calendar";
import moment from "moment";
import { MdRefresh } from "react-icons/md";
import { FaCircleArrowDown, FaCircleArrowUp } from "react-icons/fa6";
import { TbEdit } from "react-icons/tb";
import {
  Calendar as CalendarIcon,
  ChevronDown,
  Info,
  Search as SearchIcon,
  X,
} from "lucide-react";
import { GetProductsToOrder } from "../../../server/ProductToOrder";
import { orderGet, updateOrderStatus } from "../../../server/Order";
import {
  getTransactionsByOrders,
  getTransactionPaymentLabel,
  parseTransactionsByOrdersResponse,
} from "../../../server/Transactions";
import { updateOrder } from "../../../server/ActiveOrder";
import { setPreload } from "../../../server/CartsOpen";
import { useDispatch, useSelector } from "react-redux";
import PopUp from "../../containers/PopUp";
import ListPagination from "../../containers/ListPagination";
import { useListPagination } from "../../../hooks/useListPagination";
import { ALL_STATUSES, getStatusText } from "../../../utils/orderStatus";
import { getPaymentStatusText } from "../../../utils/paymentStatus";
import { formatPrice, roundMoney } from "../../../utils/formatPrice";

const AllOrders = () => {
  const dispatch = useDispatch();
  const stationId = useSelector((state) => state.user.stationId);
  const getOrderSums = (order) => {
    const productsSum = order.totalSum || 0;
    const coupon = order.couponDiscount || 0;

    const beforeCoupon = roundMoney(productsSum);
    const afterCoupon = roundMoney(productsSum - coupon);

    return { beforeCoupon, afterCoupon };
  };

  const [date, setDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [search, setSearch] = useState("");
  const [showToCalendar, setShowToCalendar] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const [activeOrder, setActiveOrder] = useState(false);
  const [openInfo, setOpenInfo] = useState(false);
  const [dataElement, setDataElement] = useState({});

  const [filterStatus, setFilterStatus] = useState("");
  const [filterLateOrders, setFilterLateOrders] = useState(false);
  const [filterPaymentStatus, setFilterPaymentStatus] = useState("");

  const [editingStatusId, setEditingStatusId] = useState(null);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);

  const applyFilters = useCallback(() => {
    let filtered = [...items];

    if (search.trim()) {
      const lower = search.toLowerCase();
      filtered = filtered.filter((x) =>
        (x.name || "").toLowerCase().includes(lower),
      );
    }

    if (filterStatus) {
      filtered = filtered.filter((x) => {
        const raw = (
          x.tenantStatus ||
          x.tenantBranchStatus ||
          ""
        ).toUpperCase();
        return raw.includes(filterStatus);
      });
    }

    if (filterPaymentStatus)
      filtered = filtered.filter(
        (x) => x.PaymentStatus === filterPaymentStatus,
      );

    if (filterLateOrders)
      filtered = filtered.filter((x) => x.dateTimeStatus === 3);

    setFilteredItems(filtered);
  }, [items, search, filterStatus, filterPaymentStatus, filterLateOrders]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const getItems = useCallback(
    (dateFrom, dateTo) => {
      let fromArr = dateFrom.split("/");
      let toArr = dateTo.split("/");
      const fromIso = fromArr[2] + "-" + fromArr[1] + "-" + fromArr[0];
      const toDateObj = new Date(
        Number(toArr[2]),
        Number(toArr[1]) - 1,
        Number(toArr[0]),
      );
      toDateObj.setDate(toDateObj.getDate() + 1);
      const toYear = toDateObj.getFullYear();
      const toMonth = String(toDateObj.getMonth() + 1).padStart(2, "0");
      const toDay = String(toDateObj.getDate()).padStart(2, "0");
      const toIso = `${toYear}-${toMonth}-${toDay}`;

      let val = {
        from: fromIso,
        to: toIso,
        stationId: stationId,
      };
      dispatch(setPreload(true));
      orderGet(val)
        .then(async (x) => {
          console.log("RAW ORDERS FROM SERVER:", x.data.orders);

          const orders = Array.isArray(x.data.orders) ? x.data.orders : [];

          setItems(orders);
          setLastRefresh(new Date().toLocaleString("he-IL"));

          const orderIds = orders.map((o) => String(o.id));
          if (!orderIds.length) return;

          try {
            const txRes = await getTransactionsByOrders(orderIds);
            const byOrderId = parseTransactionsByOrdersResponse(txRes.data);
            setItems((prev) =>
              prev.map((o) => ({
                ...o,
                transactions: byOrderId[String(o.id)] ?? [],
              })),
            );
          } catch (err) {
            console.log("error fetching transactions:", err);
          }
        })
        .catch((err) => {
          console.log("error fetching orders:", err);
        })
        .finally(() => dispatch(setPreload(false)));
    },
    [dispatch, stationId],
  );

  const fetchOrders = useCallback(() => {
    const fromDate = date.toLocaleDateString("ru-RU").split(".").join("/");
    const toDateStr = toDate.toLocaleDateString("ru-RU").split(".").join("/");
    getItems(fromDate, toDateStr);
    setTimeout(() => window.scrollTo(0, 0), 100);
  }, [date, toDate, getItems]);

  const fetchOrdersRef = useRef(fetchOrders);
  fetchOrdersRef.current = fetchOrders;

  useEffect(() => {
    fetchOrdersRef.current();
  }, [stationId]);

  const setSearchFun = (e) => {
    setSearch(e.target.value);
  };

  const clearSearch = () => {
    setSearch("");
  };

  const calendarChange = (selectedDate, action) => {
    if (action === "from") {
      setDate(selectedDate);
      setShowCalendar(false);
    } else {
      setToDate(selectedDate);
      setShowToCalendar(false);
    }
  };

  const selectDate = (action) => {
    if (action === "from") {
      setShowCalendar(!showCalendar);
      setShowToCalendar(false);
    } else {
      setShowToCalendar(!showToCalendar);
      setShowCalendar(false);
    }
  };

  const applyDateFilter = () => {
    fetchOrders();
  };

  const showInfo = (element) => {
    setOpenInfo(true);
    setDataElement(element);
  };

  // const setActiveOrderFun = (id) => {
  //   console.log("1setActiveOrderFun id:", id);
  //   if (activeOrder !== id) {
  //         console.log("2setActiveOrderFun id:", id);
  //     let findInd = items.findIndex((x) => x.id === id);
  //     if (findInd !== -1 && !items[findInd].orderItems) {
  //           console.log("3setActiveOrderFun id:", id);
  //       GetProductsToOrder(id)
  //         .then((x) => {
  //               console.log("4setActiveOrderFun id:", id);
  //           const itemsEdit = [...items];
  //           itemsEdit[findInd].orderItems = x.data;
  //           setItems(itemsEdit);
  //           setActiveOrder(id);
  //         })
  //         .catch(() => console.log("error"));
  //     } else {
  //       setActiveOrder(id);
  //     }
  //   } else {
  //     setActiveOrder(false);
  //   }
  // };
  const setActiveOrderFun = (id) => {
    if (activeOrder !== id) {
      const findInd = items.findIndex((x) => x.id === id);
      if (findInd !== -1 && !items[findInd].orderItems) {
        console.log(
          "Fetching products for order id:",
          id,
          "stationId:",
          stationId,
        );
        GetProductsToOrder(id, stationId)
          .then((response) => {
            const productsArray = Array.isArray(response.data)
              ? response.data
              : response.data.orderItems || [];

            const itemsEdit = [...items];
            const order = itemsEdit[findInd];

            // ממפה את המוצרים
            order.orderItems = productsArray.map((item) => ({
              name: item.product?.name || "מוצר לא נמצא",
              quantity: item.quantity,
              totalPrice: item.totalPrice || item.price * item.quantity,
              Price: item.price,
              productId: item.product.gtin,
              Id: item.productId,
              product: item.product,
              Comment: item.Comment || "",
            }));

            setItems(itemsEdit);
            setActiveOrder(id);
          })
          .catch(() => {});
      } else {
        setActiveOrder(id);
      }
    } else {
      setActiveOrder(false);
    }
  };
  // Reorder uses betweenById only. Rows at index 0–1 / last two cannot move yet;
  // enable those buttons when the API supports beforeById / afterById.
  const moveOrder = async (id, direction) => {
    const orderIndex = filteredItems.findIndex((order) => order.id === id);
    if (orderIndex === -1) return;

    const newFilteredList = [...filteredItems];

    if (direction === "up" && orderIndex > 1) {
      const neighborAbove = filteredItems[orderIndex - 1];
      const current = filteredItems[orderIndex];
      const rankBody = {
        kind: "betweenById",
        before: String(filteredItems[orderIndex - 2].id),
        after: String(neighborAbove.id),
      };

      [newFilteredList[orderIndex], newFilteredList[orderIndex - 1]] = [
        newFilteredList[orderIndex - 1],
        newFilteredList[orderIndex],
      ];

      try {
        await updateOrder(String(current.id), rankBody);
        setFilteredItems(newFilteredList);

        const newItems = [...items];
        const origIndex1 = newItems.findIndex(
          (o) => o.id === newFilteredList[orderIndex - 1].id,
        );
        const origIndex2 = newItems.findIndex(
          (o) => o.id === newFilteredList[orderIndex].id,
        );
        if (origIndex1 !== -1 && origIndex2 !== -1) {
          [newItems[origIndex1], newItems[origIndex2]] = [
            newItems[origIndex2],
            newItems[origIndex1],
          ];
          setItems(newItems);
        }
      } catch (error) {
        console.error("Error moving order:", error);
      }
    } else if (direction === "down" && orderIndex < filteredItems.length - 2) {
      const current = filteredItems[orderIndex];
      const neighborBelow = filteredItems[orderIndex + 1];
      const rankBody = {
        kind: "betweenById",
        before: String(neighborBelow.id),
        after: String(filteredItems[orderIndex + 2].id),
      };

      [newFilteredList[orderIndex], newFilteredList[orderIndex + 1]] = [
        newFilteredList[orderIndex + 1],
        newFilteredList[orderIndex],
      ];

      try {
        await updateOrder(String(current.id), rankBody);
        setFilteredItems(newFilteredList);

        const newItems = [...items];
        const origIndex1 = newItems.findIndex(
          (o) => o.id === newFilteredList[orderIndex + 1].id,
        );
        const origIndex2 = newItems.findIndex(
          (o) => o.id === newFilteredList[orderIndex].id,
        );
        if (origIndex1 !== -1 && origIndex2 !== -1) {
          [newItems[origIndex1], newItems[origIndex2]] = [
            newItems[origIndex2],
            newItems[origIndex1],
          ];
          setItems(newItems);
        }
      } catch (error) {
        console.error("Error moving order:", error);
      }
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    if (!newStatus) {
      setEditingStatusId(null);
      return;
    }

    const orderIndex = items.findIndex((o) => o.id === orderId);
    if (orderIndex === -1) {
      setEditingStatusId(null);
      return;
    }

    const previousStatus =
      items[orderIndex].tenantStatus || items[orderIndex].tenantBranchStatus;
    if (newStatus === previousStatus) {
      setEditingStatusId(null);
      return;
    }

    setUpdatingStatusId(orderId);

    const updatedItems = [...items];
    updatedItems[orderIndex] = {
      ...updatedItems[orderIndex],
      tenantStatus: newStatus,
    };
    setItems(updatedItems);

    try {
      await updateOrderStatus(orderId, newStatus, stationId);
    } catch (error) {
      console.error("Error updating order status:", error);
      const revertedItems = [...items];
      revertedItems[orderIndex] = {
        ...revertedItems[orderIndex],
        tenantStatus: previousStatus,
      };
      setItems(revertedItems);
      alert("שגיאה בעדכון סטטוס ליקוט");
    } finally {
      setUpdatingStatusId(null);
      setEditingStatusId(null);
    }
  };

  const clearFilters = () => {
    setSearch("");
    setFilterStatus("");
    setFilterLateOrders(false);
    setFilterPaymentStatus("");
  };

  const uniquePaymentStatuses = [
    ...new Set(items.map((item) => item.PaymentStatus).filter(Boolean)),
  ];

  const {
    toShow,
    pCounter,
    cFrom,
    cTo,
    isRowVisible,
    showPagination,
    showPaggination,
    UpPaggination,
    downPaggination,
    toShowFun,
  } = useListPagination(filteredItems.length);

  return (
    <div className="all-orders">
      <h1 className="title-role">כל ההזמנות</h1>

      <div className="orders-header">
        <div className="header-content">
          <button onClick={fetchOrders} title="רענן">
            <MdRefresh />
          </button>
          {lastRefresh && (
            <span>
              שעת ריענון: {lastRefresh} | כמות הזמנות: {filteredItems.length}{" "}
              מתוך {items.length}
            </span>
          )}
        </div>
      </div>

      <div className="filters-container">
        <div className="filter-row">
          <div className="open-calendar">
            <button className="toggle" onClick={() => selectDate("from")}>
              <CalendarIcon aria-hidden size={20} strokeWidth={2} />
              <span>
                מתאריך: {date.toLocaleDateString("he-IL").split(".").join("/")}
              </span>
            </button>
            <Calendar
              onChange={(date) => calendarChange(date, "from")}
              value={date}
              calendarType="Hebrew"
              locale="he-IL"
              className={showCalendar ? "active" : null}
            />
          </div>
          <div className="open-calendar">
            <button className="toggle" onClick={() => selectDate("to")}>
              <CalendarIcon aria-hidden size={20} strokeWidth={2} />
              <span>
                עד תאריך:{" "}
                {toDate.toLocaleDateString("he-IL").split(".").join("/")}
              </span>
            </button>
            <Calendar
              onChange={(date) => calendarChange(date, "to")}
              value={toDate}
              calendarType="Hebrew"
              locale="he-IL"
              className={showToCalendar ? "active" : null}
            />
          </div>

          <button className="apply-date-btn" onClick={applyDateFilter}>
            החל תאריכים
          </button>

          <div className="search">
            <input
              onChange={setSearchFun}
              value={search}
              type="text"
              placeholder="שם לקוח..."
            />
            {search ? (
              <button
                type="button"
                className="close"
                onClick={clearSearch}
                aria-label="נקה חיפוש"
                title="נקה חיפוש"
              >
                <X aria-hidden size={20} strokeWidth={2} />
              </button>
            ) : (
              <span className="search-icon" aria-hidden>
                <SearchIcon size={20} strokeWidth={2} />
              </span>
            )}
          </div>
        </div>

        <div className="filter-row">
          <div className="filter-select">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">כל סטטוסי הליקוט</option>
              {ALL_STATUSES.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-select">
            <select
              value={filterPaymentStatus}
              onChange={(e) => setFilterPaymentStatus(e.target.value)}
            >
              <option value="">כל סטטוסי התשלום</option>
              {uniquePaymentStatuses.map((status, index) => (
                <option key={index} value={status}>
                  {getPaymentStatusText(status)}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-checkbox">
            <label>
              <input
                type="checkbox"
                checked={filterLateOrders}
                onChange={(e) => setFilterLateOrders(e.target.checked)}
              />
              <span>הזמנות באיחור בלבד</span>
            </label>
          </div>

          <button className="clear-filters-btn" onClick={clearFilters}>
            נקה פילטרים
          </button>
        </div>
      </div>

      {!filteredItems.length ? (
        <h1 className="no-products">לא נמצאו הזמנות התואמות את הפילטרים</h1>
      ) : (
        <>
          <ListPagination
            className="history-pagination"
            showPagination={showPagination}
            toShow={toShow}
            toShowFun={toShowFun}
            cFrom={cFrom}
            cTo={cTo}
            pCounter={pCounter}
            showPaggination={showPaggination}
            downPaggination={downPaggination}
            UpPaggination={UpPaggination}
          />
          <div className="history-table-scroll">
            <table>
              <thead>
                <tr>
                  <th>מיון</th>
                  <th></th>
                  <th>#</th>
                  <th>שם לקוח</th>
                  <th>זמן ותאריך</th>
                  {/* <th>מס מוצרים</th> */}
                  <th>סכום הזמנה</th>
                  <th>סכום לוקט</th>
                  <th>פרטי לקוח</th>
                  <th>סטטוס ליקוט</th>
                  <th>סטטוס חיוב</th>
                  <th>הערת לקוח</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((element, index) => {
                  if (!isRowVisible(index)) return null;

                  // Disabled at list edges until beforeById / afterById are available.
                  const canMoveUp = index > 1;
                  const canMoveDown = index < filteredItems.length - 2;

                  return (
                    <Fragment key={element.id}>
                      <tr
                        className={
                          activeOrder === element.id ? "item active" : "item"
                        }
                      >
                        <td>
                          <div className="move-buttons">
                            <FaCircleArrowUp
                              className={`icon ${!canMoveUp ? "disabled" : ""}`}
                              onClick={() =>
                                canMoveUp && moveOrder(element.id, "up")
                              }
                            />
                            <FaCircleArrowDown
                              className={`icon ${!canMoveDown ? "disabled" : ""}`}
                              onClick={() =>
                                canMoveDown && moveOrder(element.id, "down")
                              }
                            />
                          </div>
                        </td>
                        <td>
                          <button
                            type="button"
                            onClick={() => setActiveOrderFun(element.id)}
                            className="expand-icon"
                            aria-label="פתח פרטי הזמנה"
                            title="פתח פרטי הזמנה"
                          >
                            <ChevronDown
                              aria-hidden
                              size={20}
                              strokeWidth={2}
                            />
                          </button>
                        </td>
                        <td>
                          <p>{element.id}</p>
                        </td>
                        <td>
                          <p>
                            {element.name ? element.name : element.CustomerName}
                          </p>
                        </td>
                        <td>
                          <p>
                            {moment(element.created).format("DD/MM/YY HH:mm")}
                          </p>
                        </td>
                        {/* <td>
                      <p>{element.orderItems?.length}</p>
                    </td> */}
                        <td>
                          {element.couponDiscount ? (
                            <div className="price-details">
                              <p className="price original-price">
                                {getOrderSums(element).beforeCoupon.toFixed(2)}
                              </p>
                              <p className="price ">
                                {getOrderSums(element).afterCoupon.toFixed(2)}
                              </p>
                            </div>
                          ) : (
                            <p className="price">
                              {(element.totalSum || 0).toFixed(2)}
                            </p>
                          )}
                        </td>
                        <td>
                          <p className="price">
                            {Number(element.finalTotalSum ?? 0).toFixed(2)}
                          </p>
                        </td>
                        <td>
                          <button
                            type="button"
                            onClick={() => showInfo(element)}
                            className="info-icon"
                            aria-label="מידע"
                            title="מידע"
                          >
                            <Info aria-hidden size={20} strokeWidth={2} />
                          </button>
                        </td>
                        <td>
                          <div className="status-cell">
                            {editingStatusId === element.id ? (
                              <select
                                autoFocus
                                disabled={updatingStatusId === element.id}
                                value={
                                  element.tenantStatus ||
                                  element.tenantBranchStatus ||
                                  ""
                                }
                                onChange={(e) =>
                                  handleStatusChange(element.id, e.target.value)
                                }
                                onBlur={() => setEditingStatusId(null)}
                              >
                                <option value="">בחר סטטוס</option>
                                {ALL_STATUSES.map((status) => (
                                  <option
                                    key={status.value}
                                    value={status.value}
                                  >
                                    {status.label}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <>
                                <p>
                                  {getStatusText(
                                    element.tenantStatus ||
                                      element.tenantBranchStatus,
                                  )}
                                </p>
                                <TbEdit
                                  className="edit-icon"
                                  onClick={() => setEditingStatusId(element.id)}
                                  title="עריכת סטטוס ליקוט"
                                  aria-label="עריכת סטטוס ליקוט"
                                />
                              </>
                            )}
                          </div>
                        </td>
                        <td>
                          <p>
                            {getTransactionPaymentLabel(
                              element.transactions,
                              element.PaymentStatus,
                            )}
                          </p>
                        </td>
                        <td>
                          <p className="customer-comment">
                            {element.comment || "-"}
                          </p>
                        </td>
                      </tr>
                      {activeOrder === element.id &&
                      element.orderItems &&
                      element.orderItems.length ? (
                        <>
                          <tr className="product-list">
                            <td></td>
                            <td></td>
                            <td>
                              <p>שם מוצר</p>
                            </td>
                            <td>
                              <p>ברקוד</p>
                            </td>
                            <td>
                              <p>כמות</p>
                            </td>
                            <td>
                              <p>מחיר</p>
                            </td>
                            <td colSpan="6">
                              <p>הערות</p>
                            </td>
                          </tr>
                          {element.orderItems.map((el) => {
                            return (
                              <tr key={el.Id} className="product">
                                <td></td>
                                <td></td>
                                <td>
                                  <p>{el.name}</p>
                                </td>
                                <td>
                                  <p>{el.productId}</p>
                                </td>
                                <td>
                                  <p>{el.quantity}</p>
                                </td>
                                <td>
                                  <p className="price">
                                    {formatPrice(el.totalPrice)}
                                  </p>
                                </td>
                                <td colSpan="6">
                                  <p>{el.Comment}</p>
                                </td>
                              </tr>
                            );
                          })}
                          {element.couponDiscount && (
                            <tr className="product coupon-row">
                              <td></td>
                              <td></td>
                              <td>הנחת קופון</td>
                              <td></td>
                              <td></td>
                              <td className="price">
                                -{element.couponDiscount.toFixed(2)}
                              </td>
                              <td></td>
                              <td colSpan="4"></td>
                            </tr>
                          )}
                        </>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {openInfo ? (
        <PopUp width="600px" close={() => setOpenInfo(false)}>
          <div className="comment-popup">
            <p>
              שם:{" "}
              {dataElement.name ? dataElement.name : dataElement.CustomerName}
            </p>
            <p>
              מייל: {dataElement.email ? dataElement.email : dataElement.Email}
            </p>
            <p>
              טלפון: {dataElement.phone ? dataElement.phone : dataElement.Phone}
            </p>
            <p>
              כתובת משלוח:{" "}
              {dataElement.address
                ? dataElement.address
                : dataElement.FullAddress}
            </p>
            {dataElement.comment ? <p>הערה: {dataElement.comment}</p> : null}
          </div>
        </PopUp>
      ) : null}
    </div>
  );
};

export default AllOrders;
